/**
 * 用户状态管理
 * 包含用户信息、登录状态等
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Ref } from 'vue';
import { getUserInfo, userLogout } from '@/api/auth';
import type { User } from '@/types/user';
import { clearToken } from '@/utils/auth';

// 初始用户信息
const INIT_USER_INFO: Partial<User> = {
  nickname: '',
  avatar_url: '',
};

// 用户store类型定义
interface UserStoreState {
  userInfo: Ref<User>;
  userId: Ref<string>;
  teachId: Ref<string>;
  avatarUrl: Ref<string>;
  nickName: Ref<string>;
  isLogin: Ref<boolean>;
}

interface UserStoreActions {
  setUserInfo: (info: Partial<User>) => Promise<void>;
  setUserId: (id: string) => void;
  setTeachId: (id: string) => void;
  getUserInfoAction: () => Promise<void>;
  clearUserInfo: () => void;
  logout: () => void;
}

// 创建用户store的工厂函数
function createUserStore(storeId: string, storeName: string) {
  return defineStore(
    storeId,
    (): UserStoreState & UserStoreActions => {
      // 状态
      const userInfo = ref<User>({ ...INIT_USER_INFO } as User);
      const userId = ref('');
      const teachId = ref('');
      const avatarUrl = ref('');
      const nickName = ref('');
      const isLogin = ref(false);

      // 设置用户信息
      const setUserInfo = async (info: Partial<User>) => {
        userInfo.value = { ...userInfo.value, ...info };
      };

      // 设置用户ID
      const setUserId = (id: string) => {
        userId.value = id;
      };
      const setTeachId = (id: string) => {
        teachId.value = id
      }
      // 获取用户信息
      const getUserInfoAction = async () => {
        if (!userId.value) {
          console.warn(`${storeName}: 用户ID为空，无法获取用户信息`);
          return;
        }

        try {
          const response = await getUserInfo({ id: userId.value });

          if (response.ErrCode === 0) {
            const userData = response.Result;
            userInfo.value = { ...userInfo.value, ...userData };
            console.log(`${storeName}: 用户信息获取成功`, userInfo.value);
            avatarUrl.value = userInfo.value.avatar_url || '';
            nickName.value = userInfo.value.nickname || '';
            isLogin.value = true;
          } else {
            console.error(`${storeName}: 获取用户信息失败`, response.ErrMsg);
            isLogin.value = false;
          }
        } catch (error) {
          console.error(`${storeName}: 获取用户信息异常`, error);
          clearUserInfo();
          isLogin.value = false;
          throw error;
        }
      };

      // 清除用户信息
      const clearUserInfo = () => {
        userInfo.value = { ...INIT_USER_INFO } as User;
        clearToken();
        console.log(`${storeName}: 用户信息已清除`);
      };

      // 退出登录
      const logout = () => {
        userLogout(userId.value);
        clearUserInfo();
        console.log(`${storeName}: 用户已退出登录`);
      };

      return {
        // 状态
        userInfo,
        userId,
        teachId,
        avatarUrl,
        nickName,
        isLogin,
        // 方法
        setUserInfo,
        setUserId,
        setTeachId,
        getUserInfoAction,
        clearUserInfo,
        logout,
      };
    },
    {
      persist: {
        key: storeId,
      },
    },
  );
}

// 普通用户store
export const useUserStore = createUserStore('user', '普通用户');

// 助教用户store
export const useTeachUserStore = createUserStore('userTeach', '助教用户');
